import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  EntryCell,
  EntryGrid,
  EntryRow,
  GridAction,
  GridInput,
  GridSelect,
  Input,
  ScanBar,
  Select,
  StatTile,
  Textarea,
  addDecimal,
  cmpDecimal,
  divDecimal,
  formatDate,
  formatMoney,
  formatPercent,
  formatQty,
  formatRate,
  humanizeStatus,
  isDecimal,
  isZeroDecimal,
  mulDecimal,
  roundDecimalString,
  subDecimal,
  sumDecimal,
  type ScanHit,
} from '@biovet/ui';
import { auth } from '../lib/actor.js';
import { apiGet, apiSend, useResource } from '../lib/api.js';
import {
  allCurrencies,
  baseCurrency,
  company,
  companyToday,
  monthOf,
  uomOptions,
} from '../lib/reference-data.js';
import type { RecordSet } from '../lib/design-data.js';
import { RecordGrid } from './Records.js';
import { RegisterWindow } from './parts.js';
import { printDocument, type PrintDocument } from '../lib/print.js';
import {
  ComboField,
  DocumentPanel,
  EntryPanel,
  FieldRow,
  Messages,
  PanelToggle,
  SelectField,
  SubmitHint,
  TextField,
  Tiles,
  today,
  useAction,
  useSubmitShortcut,
} from './document.js';

/**
 * Sales, returns and the commission run, reading and writing PostgreSQL.
 *
 * A sale is two postings that happen together and answer different questions:
 * the revenue posting is what the customer owes, the cost posting is what left
 * the warehouse — and the second is valued by the stock ledger's weighted
 * average, not by anything on the invoice. That is why the margin column on the
 * register is a fact rather than an estimate.
 *
 * Commission accrues on the invoice and posts at the run. The rule live on the
 * invoice date is resolved once and frozen onto the line, so a rate change next
 * quarter cannot rewrite this month's statement, and a return in the same period
 * nets against the accrual instead of chasing a voucher that has already posted.
 */

const R = 'right' as const;

interface SalesRefs {
  customers: Array<{
    code: string;
    name: string;
    ntn: string | null;
    strn: string | null;
    isSalesTaxRegistered: boolean;
    paymentTermsDays: number;
    creditLimit: string;
    currency: string;
    zoneCode: string | null;
    zoneName: string | null;
    receivableAccount: string | null;
    outstandingBase: string;
    oldestOverdueDays: number;
  }>;
  salespersons: Array<{
    code: string;
    name: string;
    zoneCode: string | null;
    zoneName: string | null;
    defaultBasis: string | null;
    defaultRate: string | null;
    recognition: string;
    monthlyTarget: string | null;
  }>;
  warehouses: Array<{ code: string; name: string; kind: string; isDefault: boolean }>;
  priceList: Array<{ sku: string; uomCode: string; unitRate: string; maxDiscountPct: string }>;
  reasonCodes: Array<{ code: string; name: string; isResaleable: boolean }>;
  zones: Array<{ code: string; name: string; depth: number; path: string }>;
  /** The city register with the zone each one sits in — see the invoice pickers. */
  cities: Array<{ code: string; name: string; zoneCode: string | null; zoneName: string | null }>;
}

// ═════════════════════════════════════════════════════════════════════════════
// Sales invoice
// ═════════════════════════════════════════════════════════════════════════════

interface InvoiceRow {
  invoiceNo: string;
  invoiceDate: string;
  dueDate: string | null;
  customerCode: string;
  customer: string;
  zoneName: string | null;
  salesperson: string | null;
  salespersonCode: string | null;
  warehouseCode: string | null;
  currency: string;
  fxRate: string;
  paymentMode: string;
  status: string;
  subtotal: string;
  discountTotal: string;
  taxTotal: string;
  freightTotal: string;
  grandTotal: string;
  grandTotalBase: string;
  cogsTotalBase: string;
  grossMarginBase: string;
  outstandingBase: string;
  voucherNo: string | null;
  narration: string | null;
  lineCount: number;
  qtyBase: string;
  commissionBase: string;
  returnedQtyBase: string;
}

/**
 * A line is now made by resolving an item, not by choosing one.
 *
 * It therefore carries what the resolver already knew — the name, the lot, what
 * is on hand — rather than looking those up again per render against a stock
 * list that may not even cover the warehouse a batch label named.
 */
interface InvoiceLineDraft {
  sku: string;
  productName: string;
  enteredQty: string;
  uomCode: string;
  unitRate: string;
  discountPct: string;
  taxPct: string;
  batchNo: string;
  expiryDate: string | null;
  /** Base units available when the line was made — the short-stock check. */
  availableQty: string;
  /** Base units in one `uomCode`, so the check can compare like with like. */
  baseFactor: string;
  /** The product's own unit — what narrows the unit picker to what will post. */
  baseUom: string;
  /** One scan's worth, in `uomCode`. Re-scanning adds exactly this. */
  scanQty: string;
}

function lineFromHit(hit: ScanHit): InvoiceLineDraft {
  return {
    sku: hit.sku,
    productName: hit.productName,
    enteredQty: hit.scanQty,
    uomCode: hit.scanUom,
    unitRate: hit.unitRate ?? hit.movingAvgRate,
    discountPct: '0',
    taxPct: hit.salesTaxPct ?? '0',
    // FEFO decides when the line does not name a lot, and the resolver has
    // already worked out which lot that is — showing it beats an empty box that
    // says "FEFO" and leaves the operator to trust it.
    batchNo: hit.batchNo ?? '',
    expiryDate: hit.expiryDate,
    availableQty: hit.availableQty,
    baseFactor: hit.baseFactor,
    baseUom: hit.baseUom,
    scanQty: hit.scanQty,
  };
}

/** One `label … value` row of the billing summary. */
function BillLine({
  label,
  value,
  muted,
  small,
}: {
  label: string;
  value: string;
  muted?: boolean;
  small?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span
        className={`${small ? 'text-[11px]' : 'text-[12.5px]'} ${
          muted ? 'text-[var(--ink-3)]' : 'text-[var(--ink-2)]'
        }`}
      >
        {label}
      </span>
      <span
        className={`bb-num tabular-nums ${small ? 'text-[11px]' : 'text-[13px]'} ${
          muted ? 'text-[var(--ink-3)]' : 'text-[var(--ink)]'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

const INVOICE_FILTERS: Array<{ label: string; test: (i: InvoiceRow) => boolean }> = [
  { label: 'All', test: () => true },
  { label: 'Outstanding', test: (i) => cmpDecimal(i.outstandingBase, '0') > 0 },
  { label: 'Cash', test: (i) => i.paymentMode === 'CASH' },
  { label: 'With returns', test: (i) => cmpDecimal(i.returnedQtyBase, '0') > 0 },
];

/**
 * The sales register, and the invoice form.
 *
 * Two things the design insists on and this keeps. The **credit check runs at
 * customer selection**, not behind a Post-time dialog: telling somebody they
 * have breached a limit after twelve lines have been keyed is telling them too
 * late. And **the batch is FEFO unless the line names one** — a bag that expires
 * in March loses value every week it stays, and shipping the newest lot first is
 * how a warehouse ends up writing off the oldest.
 *
 * Commission is shown per line because the rule can differ by product, and a
 * total that cannot be traced to a line is a total that gets argued about at
 * month end.
 */
export function SalesInvoiceScreen() {
  const [range, setRange] = useState(() => monthOf(companyToday()));
  const res = useResource<{ invoices: InvoiceRow[] }>('/invoices', {
    from: range.from || undefined,
    to: range.to || undefined,
  });
  const refs = useResource<SalesRefs>('/sales-refs');
  const all = useMemo(() => res.data?.invoices ?? [], [res.data]);
  const [chip, setChip] = useState(0);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const action = useAction(res.reload);

  const [header, setHeader] = useState({
    invoiceDate: today(),
    customerCode: '',
    warehouse: '',
    salespersonCode: '',
    /**
     * Constant, and stated rather than dropped. Every invoice this screen raises
     * is on credit — see the note on the panel header for why `CASH` went — and
     * `sales.ts` defaults to the same value, so sending it is a claim the two
     * agree rather than a field somebody has to keep in step.
     */
    paymentMode: 'CREDIT',
    /**
     * Where this sale is filed on the map.
     *
     * `sales_invoices.zone_id` has existed since `0010` and was derived from the
     * customer's zone, else the salesperson's — a guess, never shown, and the
     * figure `salesByZone` groups every rupee of revenue by. A customer who
     * moves and a salesperson who covers two zones both make it wrong quietly.
     *
     * Both are chosen here now. The **city decides**: picking one fills the zone
     * from `cities.zone_id` and the two cannot then disagree, which is what the
     * server refuses a mismatched pair for. Leaving both blank is the old
     * behaviour exactly — the server falls back to the same derivation.
     */
    zoneCode: '',
    cityCode: '',
    vehicleNo: '',
    /**
     * Freight recovered from the customer, in the invoice currency.
     *
     * `sales.ts` has always accepted `freightTotal`, added it into
     * `grand_total` and raised its own credit line for it — the form simply
     * never sent one, so the figure was silently zero on every invoice this
     * screen has ever written. The billing panel names the row, so the row has
     * to be enterable: showing "Freight recovered — 0" beside a field that does
     * not exist is a total that cannot be made right.
     */
    freightTotal: '',
    /**
     * The salesperson's rate on this invoice, as a percentage of net sales.
     *
     * Blank keeps the old behaviour exactly — the server resolves
     * `resolve_commission_rule` per line against the product, its category and
     * the date. Typed, it wins outright and the figure below stops being an
     * estimate: it is the same arithmetic `accrueCommission` does, so what the
     * panel shows and what the commission run pays are one number.
     *
     * A field rather than a rule because a rate is routinely agreed per sale in
     * this trade, and a book that keeps no rate card resolved to no rule at all
     * — which accrued nothing, silently, on every invoice.
     */
    commissionPct: '',
    narration: '',
  });
  // No blank first row: the bar makes the lines, and a placeholder row that
  // cannot be filled by hand is furniture.
  const [lines, setLines] = useState<InvoiceLineDraft[]>([]);
  const [lastAdded, setLastAdded] = useState<string | null>(null);

  const detail = useResource<{ header: Record<string, string>; lines: Record<string, string>[] }>(
    expanded ? '/invoice' : null,
    { no: expanded ?? undefined },
  );

  /**
   * The invoice as a document — one description, rendered on screen and printed.
   *
   * `h.currency` rather than the base currency on every figure: an invoice
   * raised in USD is a USD document, and `grandTotal` is the transaction amount
   * while `grandTotalBase` is what the ledger booked. Printing the base figure
   * under a USD heading would be a document the customer cannot reconcile.
   */
  const invoiceDoc = useMemo<PrintDocument | null>(() => {
    const d = detail.data;
    if (!d || !expanded) return null;
    const h = d.header;
    const currency = h.currency ?? baseCurrency();
    const money = (v: string | null | undefined) => formatMoney(v ?? '0', { currency });
    return {
      title: 'Sales Invoice',
      documentNo: h.invoiceNo ?? expanded,
      facts: [
        { label: 'Date', value: formatDate(h.invoiceDate ?? '') },
        {
          label: 'Customer',
          value: `${h.customer ?? ''}${h.customerCode ? ` (${h.customerCode})` : ''}`,
        },
        { label: 'NTN', value: h.ntn ?? '' },
        { label: 'STRN', value: h.strn ?? '' },
        { label: 'Warehouse', value: h.warehouseName ?? h.warehouseCode ?? '' },
        { label: 'Salesperson', value: h.salesperson ?? '' },
        {
          label: 'Zone / city',
          value: [h.zoneName, h.cityName].filter(Boolean).join(' · '),
        },
        { label: 'Terms', value: h.paymentMode === 'CASH' ? 'Cash' : 'Credit' },
        { label: 'Due', value: h.dueDate ? formatDate(h.dueDate) : '' },
        {
          label: 'Currency',
          value: currency === baseCurrency() ? currency : `${currency} @ ${h.fxRate ?? '1'}`,
        },
      ],
      columns: [
        { label: 'Item' },
        { label: 'Batch', width: '150px' },
        { label: 'Qty', align: 'right', width: '110px' },
        { label: 'Rate', align: 'right', width: '110px' },
        { label: 'Tax', align: 'right', width: '90px' },
        { label: 'Amount', align: 'right', width: '130px' },
      ],
      rows: d.lines.map((l) => [
        `${l.sku ?? ''} · ${l.productName ?? ''}`,
        l.batchNo ?? '—',
        `${formatQty(l.enteredQty, 0)} ${l.uomCode ?? ''}`,
        formatRate(l.unitRate, 2),
        `${Number(l.taxPct ?? 0)}%`,
        money(l.lineTotal),
      ]),
      totals: [
        { label: 'Subtotal', value: money(h.subtotal) },
        ...(Number(h.discountTotal ?? 0) !== 0
          ? [{ label: 'Discount', value: `(${money(h.discountTotal)})` }]
          : []),
        ...(Number(h.taxTotal ?? 0) !== 0
          ? [{ label: 'Sales tax', value: money(h.taxTotal) }]
          : []),
        ...(Number(h.freightTotal ?? 0) !== 0
          ? [{ label: 'Freight', value: money(h.freightTotal) }]
          : []),
        { label: 'Grand total', value: money(h.grandTotal), strong: true },
        ...(currency !== baseCurrency()
          ? [{ label: `Booked in ${baseCurrency()}`, value: formatMoney(h.grandTotalBase ?? '0') }]
          : []),
        ...(Number(h.outstandingBase ?? 0) !== 0
          ? [{ label: `Outstanding (${baseCurrency()})`, value: formatMoney(h.outstandingBase) }]
          : []),
      ],
      narration: h.narration ?? null,
      notes: [h.voucherNo ? `Posted as ${h.voucherNo}.` : 'Not posted to the general ledger.'],
      signatures: ['For ' + (company()?.name ?? 'us'), 'Received in good order'],
    };
  }, [detail.data, expanded]);

  /** COGS, margin and commission: ours, and never on the customer's copy. */
  const invoiceInternal = useMemo(
    () =>
      detail.data
        ? {
            columns: [
              { label: 'COGS', align: 'right' as const, width: '120px' },
              { label: 'Margin', align: 'right' as const, width: '120px' },
              { label: 'Commission', align: 'right' as const, width: '120px' },
            ],
            rows: detail.data.lines.map((l) => [
              formatMoney(l.cogsBase ?? '0', { decimals: 0 }),
              formatMoney(l.marginBase ?? '0', { decimals: 0 }),
              formatMoney(l.commissionAmount ?? '0', { decimals: 0, dashOnZero: true }),
            ]),
          }
        : undefined,
    [detail.data],
  );

  useEffect(() => {
    const list = refs.data;
    if (!list) return;
    setHeader((h) => ({
      ...h,
      warehouse: h.warehouse || (list.warehouses.find((w) => w.isDefault)?.code ?? ''),
      salespersonCode: h.salespersonCode || (list.salespersons[0]?.code ?? ''),
    }));
  }, [refs.data]);

  const customer = (refs.data?.customers ?? []).find((c) => c.code === header.customerCode);
  const rows = useMemo(() => {
    const test = INVOICE_FILTERS[chip]?.test ?? (() => true);
    const needle = search.trim().toLowerCase();
    return all.filter(
      (i) =>
        test(i) &&
        (needle === '' ||
          [i.invoiceNo, i.customer, i.salesperson ?? ''].some((v) =>
            v.toLowerCase().includes(needle),
          )),
    );
  }, [all, chip, search]);

  /**
   * One line's three figures, exactly.
   *
   * Decimal strings throughout, never `Number`. This used to be
   * `Number(qty) * Number(rate)` and the arithmetic below it was floating point
   * all the way to the screen — which is the one thing `packages/ui/lib/decimal`
   * exists to prevent, and it matters here because these figures are what the
   * operator checks the printed invoice against. `mulDecimal` then
   * `roundDecimalString(_, 4)` is what the server's `round_money()` does, so the
   * panel and the posted document agree digit for digit.
   *
   * `dec()` guards the empty cell: a line being typed has `''` in it, and `''`
   * is not a decimal string.
   */
  const dec = (value: string) => (isDecimal(value.trim()) ? value.trim() : '0');
  /**
   * A percentage as somebody says it out loud.
   *
   * `NUMERIC(9,6)` comes back as `2.000000`, and "2.000000% of net" in a caption
   * reads as a precision claim about a number that is simply two.
   */
  const pct = (value: string) => (value.includes('.') ? value.replace(/\.?0+$/, '') : value);
  const lineNet = (l: InvoiceLineDraft) => {
    const gross = roundDecimalString(mulDecimal(dec(l.enteredQty), dec(l.unitRate)), 4);
    const discount = roundDecimalString(
      mulDecimal(mulDecimal(gross, dec(l.discountPct)), '0.01'),
      4,
    );
    const net = subDecimal(gross, discount);
    return {
      gross,
      discount,
      net,
      tax: roundDecimalString(mulDecimal(mulDecimal(net, dec(l.taxPct)), '0.01'), 4),
    };
  };

  const totals = lines.reduce(
    (acc, l) => {
      const { gross, discount, net, tax } = lineNet(l);
      return {
        gross: addDecimal(acc.gross, gross),
        discount: addDecimal(acc.discount, discount),
        net: addDecimal(acc.net, net),
        tax: addDecimal(acc.tax, tax),
      };
    },
    { gross: '0', discount: '0', net: '0', tax: '0' },
  );

  /** Gross − discount + tax + freight. The same sum `sales.ts` gives `grand_total`. */
  const netInvoice = addDecimal(addDecimal(totals.net, totals.tax), dec(header.freightTotal));

  /**
   * The tax rate to name in the totals label, when there is one to name.
   *
   * `Sales tax 18%` is only honest while every line that carries an amount is at
   * 18%. A basket mixing 18% feed with a zero-rated line has no single rate, and
   * labelling the total with whichever rate happened to be on the first line
   * would be a caption that contradicts the lines above it — so it falls back to
   * plain `Sales tax`.
   */
  const taxRate = (() => {
    const rates = new Set(
      lines.filter((l) => !isZeroDecimal(dec(l.enteredQty))).map((l) => dec(l.taxPct)),
    );
    const only = rates.size === 1 ? [...rates][0] : undefined;
    return only && !isZeroDecimal(only) ? pct(only) : null;
  })();

  /**
   * What the salesperson stands to earn — an **estimate**, and labelled as one.
   *
   * The real figure is resolved per line by `resolve_commission_rule` at posting
   * time, against the product, its category and the date, and frozen onto the
   * invoice line. The browser cannot run that resolution: it holds the
   * salesperson's *default* rate and nothing about the per-product rules that
   * override it.
   *
   * So this is computed only for `PCT_OF_NET_SALES`, the one basis whose inputs
   * are all on screen. `PCT_OF_GROSS_MARGIN` needs the COGS the stock ledger
   * decides under a row lock at posting, `PER_UNIT` needs a UoM factor, and
   * `SLAB` is achieved over a month rather than over one invoice — the server
   * leaves that one at zero on purpose. Guessing any of them would put a number
   * on screen that the commission run then contradicts, and a commission figure
   * that gets argued about at month end is worse than one that says "estimate".
   */
  const salesperson = (refs.data?.salespersons ?? []).find(
    (s) => s.code === header.salespersonCode,
  );
  /**
   * A rate typed on this invoice, if there is one — and it is not an estimate.
   *
   * `accrueCommission` takes the same percentage of the same net, so the figure
   * in the panel is the figure that accrues. Everything above about rules,
   * bases and slabs applies only to the blank case.
   */
  const typedCommissionPct = isDecimal(header.commissionPct.trim())
    ? header.commissionPct.trim()
    : null;
  const commissionRate =
    typedCommissionPct ??
    (salesperson?.defaultBasis === 'PCT_OF_NET_SALES' ? (salesperson.defaultRate ?? null) : null);
  const commission =
    commissionRate !== null
      ? roundDecimalString(mulDecimal(mulDecimal(totals.net, dec(commissionRate)), '0.01'), 4)
      : null;

  /**
   * The invoice total in each foreign currency the register carries a rate for.
   *
   * A courtesy line, and `≈` is the whole of its claim: the invoice is in the
   * currency on its header and the ledger keeps it in base, and this is neither
   * — it is "roughly what that is in dollars" for somebody who thinks in them.
   * `divDecimal` returns null on a currency whose rate has not been set, and the
   * row is dropped rather than shown at par.
   *
   * Driven by the register rather than by the word USD, because a currency added
   * under Setup → Currencies should appear here without a code change.
   */
  const approximations = allCurrencies()
    .filter((c) => !c.isBase && c.rate && !isZeroDecimal(c.rate))
    .map((c) => ({
      code: c.code,
      rate: c.rate as string,
      value: divDecimal(netInvoice, c.rate as string, 2),
    }))
    .filter((a): a is { code: string; rate: string; value: string } => a.value !== null);

  /**
   * What this customer would owe once this invoice posts, against what they may.
   *
   * **The balance is the ledger's, not the register's.** `outstandingBase` comes
   * back from `party_outstanding_base` — the sum of the debits less the credits
   * on their receivable lines across every posted voucher — so a receipt entered
   * this morning is already in it and an invoice that has not posted is not.
   * Anything else would be a second opinion about a figure the trial balance
   * already has one about.
   *
   * Decimal strings throughout, because this decides whether a document may be
   * raised: `Number('0.1') + Number('0.2')` is not a comparison to make a
   * refusal out of.
   *
   * `check_credit_limit` on the server is still the authority and still runs
   * inside the posting transaction — this exists so the refusal arrives when the
   * customer is chosen rather than after twelve lines have been keyed, which is
   * the same reason the whole of `voucher-model.ts` restates `0015_invariants`.
   */
  const creditLimit = customer?.creditLimit ?? '0';
  const hasLimit = customer !== undefined && !isZeroDecimal(dec(creditLimit));
  const exposure = addDecimal(dec(customer?.outstandingBase ?? '0'), netInvoice);
  const overLimit = hasLimit && cmpDecimal(exposure, dec(creditLimit)) > 0;

  const lookup = useCallback(
    (q: string) =>
      apiGet<{ exact: ScanHit | null; hits: ScanHit[] }>('/item-lookup', {
        q,
        warehouse: header.warehouse || undefined,
      }),
    [header.warehouse],
  );

  /**
   * Scan once, get a line. Scan the same thing again, get one more of it.
   *
   * Twelve bags of the same feed is twelve scans and one line, not twelve lines
   * — that is what the operator is doing physically, and a register that grows
   * a row per bag is a register nobody can check against the pallet. The lot is
   * part of the identity: two batches of one SKU are two lines, because they
   * leave stock at different costs and expire on different days.
   *
   * The increment goes through `addDecimal` rather than `+`. A quantity is a
   * `NUMERIC(19,4)` at the far end and floating-point addition of `0.1` twelve
   * times is not what the stock ledger will store.
   */
  const addOrBump = useCallback((hit: ScanHit) => {
    setLines((current) => {
      const at = current.findIndex(
        (l) => l.sku === hit.sku && (l.batchNo || '') === (hit.batchNo ?? ''),
      );
      if (at === -1) return [...current, lineFromHit(hit)];
      const next = [...current];
      const line = current[at] as InvoiceLineDraft;
      next[at] = {
        ...line,
        enteredQty: addDecimal(line.enteredQty || '0', hit.scanQty),
      };
      return next;
    });
    setLastAdded(`${hit.sku} · ${hit.productName}`);
  }, []);

  const postable =
    !action.busy &&
    header.customerCode !== '' &&
    header.warehouse !== '' &&
    lines.length > 0 &&
    lines.every((l) => l.enteredQty !== '' && l.unitRate !== '') &&
    // The limit is a limit, and there is no way past it from this screen. A
    // customer over their limit is invoiced by raising the limit on the customer
    // or by receiving what they owe — both of which change the fact rather than
    // the answer. `sales.ts` refuses the same document, so this only decides
    // *when* the refusal arrives, not whether.
    !overLimit;

  const save = () => {
    void action.run(() =>
      apiSend<{ invoiceNo: string; message: string }>(
        'POST',
        '/invoices',
        {
          ...header,
          salespersonCode: header.salespersonCode || undefined,
          // Blank on either is "work it out from the customer", which is what
          // the server does with an absent key — and is what every invoice
          // raised before this screen had the fields did.
          zoneCode: header.zoneCode || undefined,
          cityCode: header.cityCode || undefined,
          // Blank means none. `optionalDecimal` on the server refuses `''`.
          freightTotal: header.freightTotal.trim() || undefined,
          // Blank means "resolve the rule", which is what the server does with
          // an absent key. Sending `''` would be a 400, and sending 0 would be a
          // deliberate rate of nothing — a different instruction entirely.
          commissionPct: header.commissionPct.trim() || undefined,
          // Named explicitly: the draft also carries what the resolver told the
          // screen — the item name, what was on hand, the pack size — and none
          // of that is the invoice's to state.
          lines: lines
            .filter((l) => l.sku !== '' && l.enteredQty !== '' && l.unitRate !== '')
            .map((l) => ({
              sku: l.sku,
              enteredQty: l.enteredQty,
              uomCode: l.uomCode,
              unitRate: l.unitRate,
              discountPct: l.discountPct,
              taxPct: l.taxPct,
              batchNo: l.batchNo || undefined,
            })),
        },
        auth(),
      ).then((r) => {
        setOpen(false);
        setLines([]);
        setLastAdded(null);
        // Freight and the negotiated commission rate both belong to the invoice
        // just written. Carrying either over would apply one customer's terms to
        // the next — billing them for the last one's delivery, or paying the
        // salesperson a rate nobody agreed on this sale.
        setHeader({ ...header, freightTotal: '', commissionPct: '', zoneCode: '', cityCode: '' });
        // The customer list carries `party_outstanding_base`, which this invoice
        // has just moved — without this the credit check on the next invoice of
        // the afternoon would be measured against the balance as it stood before
        // lunch, which is the one way a limit lets something through.
        void refs.reload();
        return r;
      }),
    );
  };

  useSubmitShortcut(open && postable, save);

  const set: RecordSet = {
    crumb: 'Sales & Purchase / Sales Invoice',
    title: 'Sales register',
    subtitle: 'Every sales invoice raised, with what it cost and what it earned.',
    chips: INVOICE_FILTERS.map((f) => f.label),
    cols: [
      { label: 'Invoice', width: '150px', mono: true },
      { label: 'Date', width: '104px', mono: true },
      { label: 'Customer' },
      { label: 'Salesperson', width: '150px' },
      { label: 'Net', align: R, mono: true, width: '150px' },
      { label: 'COGS', align: R, mono: true, width: '140px' },
      { label: 'Margin', align: R, mono: true, width: '140px' },
      { label: 'Commission', align: R, mono: true, width: '130px' },
      { label: 'Outstanding', align: R, mono: true, width: '140px' },
    ],
    rows: rows.map((i) => [
      i.invoiceNo,
      formatDate(i.invoiceDate),
      i.customer,
      i.salesperson ?? '—',
      formatMoney(i.grandTotalBase, { decimals: 0 }),
      formatMoney(i.cogsTotalBase, { decimals: 0 }),
      formatMoney(i.grossMarginBase, { decimals: 0 }),
      formatMoney(i.commissionBase, { decimals: 0, dashOnZero: true }),
      formatMoney(i.outstandingBase, { decimals: 0, dashOnZero: true }),
    ]),
    total: [
      '',
      '',
      `${rows.length} invoice${rows.length === 1 ? '' : 's'}`,
      '',
      formatMoney(sumDecimal(rows.map((i) => i.grandTotalBase)), { decimals: 0 }),
      formatMoney(sumDecimal(rows.map((i) => i.cogsTotalBase)), { decimals: 0 }),
      formatMoney(sumDecimal(rows.map((i) => i.grossMarginBase)), { decimals: 0 }),
      formatMoney(sumDecimal(rows.map((i) => i.commissionBase)), { decimals: 0 }),
      formatMoney(sumDecimal(rows.map((i) => i.outstandingBase)), { decimals: 0 }),
    ],
  };

  return (
    <RecordGrid
      set={set}
      loading={res.loading}
      error={res.error}
      chip={chip}
      onChipChange={setChip}
      onRowSelect={(i) =>
        setExpanded(expanded === rows[i]?.invoiceNo ? null : (rows[i]?.invoiceNo ?? null))
      }
      emptyTitle="No invoice in this view"
      hideGrid={open}
      actions={
        <>
          {!open && (
            <Input
              size="sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Invoice or customer"
              aria-label="Search invoices"
              className="w-[200px]"
            />
          )}
          <PanelToggle open={open} onToggle={() => setOpen(!open)} label="New invoice" />
        </>
      }
    >
      <Messages notice={action.notice} problem={action.problem} />

      {/* The window bar goes with the grid: it filters rows that are not on
          screen while the entry panel is open, and a stated date range over a
          form invites the reading that the form is filtered. */}
      {!open && (
        <RegisterWindow range={range} onChange={setRange} count={rows.length} noun="invoice" />
      )}

      <EntryPanel
        title="Sales invoice"
        subtitle="One bar: scan a label, or type a SKU, item, brand or batch. The lot is the earliest expiry unless the label named one."
        open={open}
        onToggle={() => setOpen(false)}
        openLabel="New invoice"
        /*
          Post sits in the panel header, top right.

          It was at the foot of the form, under the totals, which put the button
          that commits the document below everything the document is — so on an
          invoice of any length it was off screen. Header actions are the one
          part of a card that does not move.

          `SubmitHint` stays with it, because Ctrl+Enter is the same act as the
          button and a shortcut named somewhere else is folklore.

          Two controls that used to sit beside it are gone.

          **The payment mode**, because `CASH` did not do what the word says. It
          changed the due date to the invoice date and nothing else: the entry
          was still Dr Accounts Receivable, so a cash sale left a receivable
          against a customer who had already paid, and the till was never
          debited. Every invoice this screen raises is on credit; a customer who
          pays at the counter is a Cash Receipt against the invoice, which is a
          real posting to a real till. `payment_mode` stays on the table and the
          register still reads `Cash` on the invoices raised before this.

          **The credit-limit badge**, which read `Credit limit 0% used` on every
          customer in a book where nobody has set a limit — a figure that looks
          like a measurement and is the absence of one. What stands in its place
          is the balance itself, below, which is a fact rather than a ratio and
          renders only once there is a customer to have one.
        */
        actions={
          <>
            {/*
              The customer's ledger balance, left of the shortcut hint.

              `party_outstanding_base` — what they owe on posted vouchers, before
              this invoice, which is the number somebody asks for out loud when
              they hear the name. It sits in the header because that is the part
              of the card that does not move: on an invoice of any length the
              customer picker has scrolled away long before Post is reached, and
              the balance is most wanted at exactly that moment.

              `dashOnZero` is deliberate: a customer who owes nothing reads `—`,
              not `0`, because a zero in a column of balances invites a second
              look to check whether it is a figure or a blank.
            */}
            {customer && (
              <span className="flex shrink-0 items-baseline gap-1.5 pr-1 text-[11.5px] whitespace-nowrap text-[var(--ink-3)]">
                Balance
                <b
                  className={`bb-num tabular-nums text-[13px] ${
                    overLimit ? 'text-[var(--danger)]' : 'text-[var(--ink)]'
                  }`}
                  title={`${customer.name} — owed on posted vouchers, before this invoice`}
                >
                  {formatMoney(customer.outstandingBase, { decimals: 0, dashOnZero: true })}
                </b>
                {hasLimit && (
                  <span className="text-[10.5px]">
                    of {formatMoney(creditLimit, { decimals: 0 })}
                  </span>
                )}
              </span>
            )}
            <SubmitHint />
            <Button size="sm" variant="primary" disabled={!postable} onClick={save}>
              Post &amp; print DN
            </Button>
          </>
        }
      >
        <FieldRow>
          <TextField
            label="Invoice date"
            type="date"
            required
            value={header.invoiceDate}
            onChange={(v) => setHeader({ ...header, invoiceDate: v })}
          />
          <ComboField
            label="Customer"
            required
            // The account this customer's balance lands on, named in the hint.
            // A customer is a level-5 account under `1131` (migration 0030), so
            // the dropdown and the chart of accounts are the same register seen
            // from two sides — and the invoice can say which row it will debit
            // before it is raised, rather than after.
            hint={
              customer
                ? `${customer.receivableAccount ?? 'no account mapped'} · outstanding ${formatMoney(customer.outstandingBase, { decimals: 0 })} · limit ${formatMoney(customer.creditLimit, { decimals: 0 })}${customer.oldestOverdueDays > 0 ? ` · ${customer.oldestOverdueDays} days overdue` : ''}`
                : undefined
            }
            value={header.customerCode}
            options={(refs.data?.customers ?? []).map((c) => ({
              value: c.code,
              label: c.name,
              hint: c.receivableAccount ?? c.zoneName ?? c.code,
              // Searchable by account code too: somebody reading the chart and
              // somebody raising the invoice are looking for the same customer.
              keywords: `${c.code} ${c.ntn ?? ''} ${c.receivableAccount ?? ''}`,
            }))}
            onChange={(v) => setHeader({ ...header, customerCode: v })}
          />
          <ComboField
            label="Warehouse"
            required
            value={header.warehouse}
            options={(refs.data?.warehouses ?? []).map((w) => ({
              value: w.code,
              label: w.name,
              hint: w.code,
            }))}
            onChange={(v) => setHeader({ ...header, warehouse: v })}
          />
          <ComboField
            label="Salesperson"
            hint="Decides which commission rule applies"
            value={header.salespersonCode}
            options={(refs.data?.salespersons ?? []).map((s) => ({
              value: s.code,
              label: s.name,
              hint: s.zoneName ?? s.code,
            }))}
            onChange={(v) => setHeader({ ...header, salespersonCode: v })}
          />
          {/*
            Zone and city, immediately after the salesperson — the three
            together are "who sold this, and where it counts".

            **The city fills the zone, and never the other way round.** A city
            sits in exactly one zone, so choosing one answers both questions;
            choosing a zone answers only the wider of the two, and clearing the
            city when it does would throw away the narrower answer somebody had
            already given. So the zone picker narrows the city list and leaves a
            city alone when it is still inside the new zone.
          */}
          <ComboField
            label="Zone"
            hint="Where this sale is filed — blank uses the customer's zone"
            value={header.zoneCode}
            options={(refs.data?.zones ?? []).map((z) => ({
              value: z.code,
              label: `${'— '.repeat(z.depth)}${z.name}`,
              hint: z.code,
            }))}
            onChange={(v) =>
              setHeader((h) => {
                const city = (refs.data?.cities ?? []).find((c) => c.code === h.cityCode);
                // A city that is not in the chosen zone is no longer an answer
                // to this invoice's question, and leaving it would send the
                // server a pair it refuses.
                const keep = v === '' || (city ? city.zoneCode === v : true);
                return { ...h, zoneCode: v, cityCode: keep ? h.cityCode : '' };
              })
            }
          />
          <ComboField
            label="City"
            hint="Sets the zone with it — a city sits in exactly one"
            value={header.cityCode}
            options={(refs.data?.cities ?? [])
              .filter((c) => header.zoneCode === '' || c.zoneCode === header.zoneCode)
              .map((c) => ({
                value: c.code,
                label: c.name,
                hint: c.zoneName ?? 'no zone',
                keywords: c.zoneCode ?? '',
              }))}
            onChange={(v) =>
              setHeader((h) => {
                const city = (refs.data?.cities ?? []).find((c) => c.code === v);
                return { ...h, cityCode: v, zoneCode: city?.zoneCode ?? h.zoneCode };
              })
            }
          />
        </FieldRow>

        <ScanBar
          lookup={lookup}
          onPick={addOrBump}
          disabled={header.warehouse === ''}
          disabledReason="Choose a warehouse first — stock is per location"
          status={
            lines.length === 0
              ? 'Scan a label or type a few letters. The item, its lot, the price and the tax all come back together.'
              : `${lines.length} line${lines.length === 1 ? '' : 's'}${lastAdded ? ` · last: ${lastAdded}` : ''} · scan the same item again to add another`
          }
        />

        <EntryGrid
          aria-label="Invoice lines"
          columns={[
            { key: 'no', label: '#', width: '44px', noPrint: true },
            { key: 'sku', label: 'Item', width: '250px' },
            { key: 'batch', label: 'Batch', width: '190px' },
            { key: 'qty', label: 'Qty', width: '110px', numeric: true, required: true },
            { key: 'uom', label: 'Unit', width: '90px' },
            { key: 'rate', label: 'Rate', width: '120px', numeric: true, required: true },
            { key: 'disc', label: 'Disc %', width: '90px', numeric: true },
            { key: 'tax', label: 'GST %', width: '90px', numeric: true },
            { key: 'amount', label: 'Amount', width: '140px', numeric: true },
            { key: 'del', label: '', width: '44px', noPrint: true },
          ]}
          footer={
            <tr>
              <td colSpan={8} className="px-[var(--pad)] py-2 text-right text-[12px]">
                Net of discount, plus output tax
              </td>
              <td className="bb-num px-2.5 py-2 text-right text-[13px] tabular-nums">
                {formatMoney(addDecimal(totals.net, totals.tax), { decimals: 0 })}
              </td>
              <td />
            </tr>
          }
        >
          {lines.map((line, i) => {
            const update = (patch: Partial<InvoiceLineDraft>) => {
              const next = [...lines];
              next[i] = { ...line, ...patch };
              setLines(next);
            };
            // Compared in base units, because that is the only unit the stock
            // ledger has an opinion about. A quantity in bags means nothing
            // until it is multiplied by what a bag holds for *this* SKU, and
            // the database worked that factor out when it resolved the item.
            const short =
              line.enteredQty !== '' &&
              cmpDecimal(
                mulDecimal(line.enteredQty || '0', line.baseFactor || '1'),
                line.availableQty || '0',
              ) > 0;
            const soon = line.expiryDate !== null;
            return (
              <EntryRow key={`${line.sku}|${line.batchNo}`} tone={short ? 'invalid' : 'default'}>
                <EntryCell padded className="text-[var(--ink-3)]">
                  {i + 1}
                </EntryCell>
                {/* Read-only: the item was resolved, not chosen, and offering a
                    dropdown here would invite somebody to change what the
                    scanner already established — losing the lot and the price
                    that came with it. Delete the line and scan again. */}
                <EntryCell padded>
                  <span className="bb-mono pr-2 text-[12px] text-[var(--accent)]">{line.sku}</span>
                  <span className="text-[var(--ink)]">{line.productName}</span>
                </EntryCell>
                <EntryCell>
                  <GridInput
                    value={line.batchNo}
                    placeholder="FEFO"
                    title={soon ? `Expires ${line.expiryDate}` : undefined}
                    aria-label={`Line ${i + 1} batch`}
                    onChange={(e) => update({ batchNo: e.target.value })}
                  />
                </EntryCell>
                <EntryCell>
                  <GridInput
                    numeric
                    invalid={short}
                    value={line.enteredQty}
                    aria-label={`Line ${i + 1} quantity`}
                    onChange={(e) => update({ enteredQty: e.target.value })}
                  />
                </EntryCell>
                <EntryCell>
                  <GridSelect
                    value={line.uomCode}
                    aria-label={`Line ${i + 1} unit`}
                    onChange={(e) => update({ uomCode: e.target.value })}
                  >
                    {/* Setup → Units of Measure, narrowed to the units this
                        item can actually be transacted in — see `uomOptions`. */}
                    {uomOptions(line.uomCode, line.baseUom).map((u) => (
                      <option key={u.code} value={u.code}>
                        {u.code}
                      </option>
                    ))}
                  </GridSelect>
                </EntryCell>
                <EntryCell>
                  <GridInput
                    numeric
                    value={line.unitRate}
                    aria-label={`Line ${i + 1} rate`}
                    onChange={(e) => update({ unitRate: e.target.value })}
                  />
                </EntryCell>
                <EntryCell>
                  <GridInput
                    numeric
                    value={line.discountPct}
                    aria-label={`Line ${i + 1} discount`}
                    onChange={(e) => update({ discountPct: e.target.value })}
                  />
                </EntryCell>
                <EntryCell>
                  <GridInput
                    numeric
                    value={line.taxPct}
                    aria-label={`Line ${i + 1} tax`}
                    onChange={(e) => update({ taxPct: e.target.value })}
                  />
                </EntryCell>
                <EntryCell padded numeric>
                  {formatMoney(lineNet(line).net, { decimals: 0, dashOnZero: true })}
                </EntryCell>
                <EntryCell noPrint>
                  <GridAction
                    label={`Remove line ${i + 1}`}
                    onClick={() => setLines(lines.filter((_, j) => j !== i))}
                  >
                    ×
                  </GridAction>
                </EntryCell>
              </EntryRow>
            );
          })}
          {lines.length === 0 && (
            <tr>
              <td
                colSpan={10}
                className="px-[var(--pad)] py-6 text-center text-[12.5px] text-[var(--ink-3)]"
              >
                No lines yet — the bar above makes them.
              </td>
            </tr>
          )}
        </EntryGrid>

        {/*
          The limit, when it is in the way.

          Nothing renders for a customer with no limit set, or for one this
          invoice keeps inside theirs — a badge reading "0% used" on every
          customer in a book where nobody has set a limit is a figure that looks
          like a measurement and is the absence of one, which is why the old
          badge was removed. What is shown is the arithmetic itself and nothing
          else: what they owe on the ledger, what this adds, what that comes to
          and what they may owe. Four figures, so the refusal can be checked
          rather than believed — and no sentence explaining how to get past it,
          because there is no way past it.
        */}
        {overLimit && customer && (
          <Callout tone="danger" title={`${customer.name} is over their credit limit`}>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-[12.5px]">
              <span>
                Ledger balance{' '}
                <b className="bb-num">{formatMoney(customer.outstandingBase, { decimals: 0 })}</b>
              </span>
              <span>
                This invoice <b className="bb-num">{formatMoney(netInvoice, { decimals: 0 })}</b>
              </span>
              <span>
                Would owe <b className="bb-num">{formatMoney(exposure, { decimals: 0 })}</b>
              </span>
              <span>
                Limit <b className="bb-num">{formatMoney(creditLimit, { decimals: 0 })}</b>
              </span>
            </div>
          </Callout>
        )}

        {/*
          The billing summary — what the customer is about to be charged, built
          up from the lines the way the printed invoice reads it.

          It sits to the right of the narration because that is where an invoice
          carries its totals, and every figure in it is derived from the grid
          above rather than typed. The one exception is freight, which is an
          input: it is a charge on the invoice that no line states.

          Every figure is the same arithmetic `sales.ts` does — gross, less
          discount, plus tax, plus freight — so the panel and the posted
          document cannot disagree.
        */}
        <div className="flex flex-col gap-3.5 lg:flex-row lg:items-stretch">
          {/*
            Narration to the left of the totals, sharing their row.

            It used to be a strip below them, which made the last thing on the
            form a text box rather than the figure the invoice is for. Beside
            them it reads the way a delivery note does — what is being sent and
            how, then what it comes to — and the two fill the same height, so
            the panel ends on a straight edge instead of a ragged one.
          */}
          <div className="flex min-w-0 flex-1 flex-col">
            <label
              htmlFor="invoice-narration"
              className="bb-eyebrow pb-1.5 text-[10px] text-[var(--ink-3)]"
            >
              Narration &amp; delivery
            </label>
            <Textarea
              id="invoice-narration"
              value={header.narration}
              placeholder="Delivery by vehicle — transporter — gate pass"
              aria-label="Narration"
              className="min-h-[120px] flex-1"
              onChange={(e) => setHeader({ ...header, narration: e.target.value })}
            />
          </div>

          <div className="w-full lg:w-[360px]">
            <div className="rounded-[var(--radius)] border border-[var(--line)] bg-[var(--surface-2)] px-3.5 py-3">
              <BillLine label="Gross" value={formatMoney(totals.gross, { decimals: 0 })} />
              <BillLine
                label="Discount"
                // Parenthesised rather than signed: an invoice states a
                // deduction as `(10,880)`, and a minus sign in a column of
                // positives reads as a correction rather than as a discount.
                value={
                  isZeroDecimal(totals.discount)
                    ? '—'
                    : `(${formatMoney(totals.discount, { decimals: 0 })})`
                }
                muted={isZeroDecimal(totals.discount)}
              />
              <BillLine
                label={taxRate ? `Sales tax ${taxRate}%` : 'Sales tax'}
                value={formatMoney(totals.tax, { decimals: 0 })}
              />

              {/* An input, not a figure — see `header.freightTotal`. */}
              <div className="flex items-center justify-between gap-3 py-1">
                <label
                  htmlFor="invoice-freight"
                  className="text-[12.5px] whitespace-nowrap text-[var(--ink-2)]"
                >
                  Freight recovered
                </label>
                <Input
                  id="invoice-freight"
                  size="sm"
                  inputMode="decimal"
                  value={header.freightTotal}
                  placeholder="0"
                  aria-label="Freight recovered"
                  title="Charged to the customer and credited to freight income — it is not apportioned over the lines"
                  className="w-[120px] text-right"
                  onChange={(e) => setHeader({ ...header, freightTotal: e.target.value })}
                />
              </div>

              <div className="mt-2 flex items-center justify-between gap-3 border-t border-[var(--line)] pt-2.5">
                <span className="text-[13px] font-semibold text-[var(--ink)]">Net invoice</span>
                <span className="bb-num text-[17px] font-semibold tabular-nums text-[var(--ink)]">
                  {formatMoney(netInvoice, { decimals: 0 })}
                </span>
              </div>

              {approximations.map((a) => (
                <BillLine
                  key={a.code}
                  label={`≈ ${a.code} @ ${formatRate(a.rate)}`}
                  value={formatMoney(a.value, { decimals: 0 })}
                  muted
                  small
                />
              ))}

              {/*
                The commission, and the rate that makes it.

                The rate is an input because it is negotiated — not per
                salesperson and not per product, but per sale — and the book had
                no way to say so: the only rate that reached `commission_entries`
                came from `resolve_commission_rule`, so a house that agrees a
                percentage on the day accrued whatever the rate card said, or
                nothing at all where there is no card.

                Typed, the figure beside it stops being an estimate: the server
                takes the same percentage of the same net. Blank, it is the rule
                again and the caption says so — the two cases are genuinely
                different claims and the panel makes exactly one of them.
              */}
              {salesperson && (
                <div className="mt-2 border-t border-[var(--line-2)] pt-2">
                  <div className="flex items-center justify-between gap-3">
                    <label
                      htmlFor="invoice-commission"
                      className="text-[12.5px] whitespace-nowrap text-[var(--ink-2)]"
                    >
                      Commission rate %
                    </label>
                    <Input
                      id="invoice-commission"
                      size="sm"
                      inputMode="decimal"
                      value={header.commissionPct}
                      placeholder={
                        salesperson.defaultBasis === 'PCT_OF_NET_SALES' && salesperson.defaultRate
                          ? pct(salesperson.defaultRate)
                          : 'rule'
                      }
                      aria-label="Salesperson commission rate percent"
                      title="Percent of net sales on this invoice. Leave blank to let the commission rule decide."
                      className="w-[120px] text-right"
                      onChange={(e) => setHeader({ ...header, commissionPct: e.target.value })}
                    />
                  </div>
                  <div className="flex items-start justify-between gap-3 pt-1">
                    <span className="text-[10.5px] text-[var(--ink-3)]">
                      {typedCommissionPct
                        ? `${pct(typedCommissionPct)}% of net on this invoice — this is what accrues.`
                        : commission
                          ? `Estimate — ${pct(salesperson.defaultRate ?? '0')}% of net. The rule resolved at posting decides.`
                          : 'Resolved per line at posting — this basis cannot be estimated here.'}
                    </span>
                    <span className="bb-num shrink-0 text-[13px] tabular-nums text-[var(--credit)]">
                      {commission ? formatMoney(commission, { decimals: 0 }) : '—'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </EntryPanel>

      {/*
        The invoice itself, read back and printable.

        This was a lines-only card with COGS, margin and commission in it and no
        way to print any of it. The three internal columns are still here and are
        still only here: they are passed as `internal`, which the panel appends
        on screen and the printer never sees, so the copy that goes to the
        customer cannot carry what the sale cost us.
      */}
      {expanded && !open && (
        <DocumentPanel
          doc={invoiceDoc}
          internal={invoiceInternal}
          loading={detail.loading}
          error={detail.error}
          onClose={() => setExpanded(null)}
          actions={
            invoiceDoc && (
              <Button size="sm" variant="primary" onClick={() => printDocument(invoiceDoc)}>
                Print invoice
              </Button>
            )
          }
        />
      )}

      {/*
        The four register totals go with the register.

        They summarise the invoices in the grid — net sales, margin, receivable
        and accrued commission across whatever the chips have filtered to — and
        none of that is about the invoice being typed. Left on screen under an
        open form they read as if they were its totals, which they are not: the
        figures for *this* invoice are in the billing panel a few inches above,
        and two sets of money on one screen answering different questions is how
        somebody posts against the wrong one.
      */}
      {!open && (
        <Tiles>
          <Card>
            <StatTile
              label="Net sales"
              value={formatMoney(sumDecimal(rows.map((i) => i.grandTotalBase)), { decimals: 0 })}
              hint="including output tax"
            />
          </Card>
          <Card>
            <StatTile
              label="Gross margin"
              value={formatMoney(sumDecimal(rows.map((i) => i.grossMarginBase)), { decimals: 0 })}
              hint="at frozen weighted-average cost"
            />
          </Card>
          <Card>
            <StatTile
              label="Receivable"
              value={formatMoney(sumDecimal(rows.map((i) => i.outstandingBase)), { decimals: 0 })}
              hint="unsettled on these invoices"
            />
          </Card>
          <Card>
            <StatTile
              label="Commission accrued"
              value={formatMoney(sumDecimal(rows.map((i) => i.commissionBase)), { decimals: 0 })}
              hint="posts with the run"
            />
          </Card>
        </Tiles>
      )}
    </RecordGrid>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Sale return
// ═════════════════════════════════════════════════════════════════════════════

interface SaleReturnRow {
  returnNo: string;
  returnDate: string;
  customerCode: string;
  customer: string;
  againstInvoice: string | null;
  warehouseCode: string;
  warehouseName: string;
  warehouseKind: string;
  reasonCode: string | null;
  reason: string | null;
  settlementMode: string;
  status: string;
  salesperson: string | null;
  subtotal: string;
  taxTotal: string;
  grandTotal: string;
  grandTotalBase: string;
  cogsTotalBase: string;
  commissionReversalBase: string;
  voucherNo: string | null;
  narration: string | null;
  qtyBase: string;
  lineCount: number;
}

/**
 * Credit notes.
 *
 * Four things reverse and they do not reverse the same way. The customer is
 * credited at the **original invoice rate**. The stock returns at the **original
 * COGS**, into the batch it left from, so a return is value-neutral to the
 * warehouse rather than moving its average. The commission is reversed **pro
 * rata** as a negative accrual the next run nets off. And goods that came back
 * unsaleable do not go back into saleable stock — the reason code decides that,
 * not the operator, and the write-off is on the same voucher.
 */
export function SaleReturnScreen() {
  const [range, setRange] = useState(() => monthOf(companyToday()));
  const res = useResource<{ returns: SaleReturnRow[] }>('/sale-returns', {
    from: range.from || undefined,
    to: range.to || undefined,
  });
  const invoices = useResource<{ invoices: InvoiceRow[] }>('/invoices');
  const refs = useResource<SalesRefs>('/sales-refs');
  const all = useMemo(() => res.data?.returns ?? [], [res.data]);
  const [chip, setChip] = useState(0);
  const [open, setOpen] = useState(false);
  const action = useAction(res.reload);

  const [against, setAgainst] = useState('');
  const [returnDate, setReturnDate] = useState(today());
  const [warehouse, setWarehouse] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [settlementMode, setSettlementMode] = useState('ADJUST_RECEIVABLE');
  const [quantities, setQuantities] = useState<Record<number, string>>({});

  const source = useResource<{ header: Record<string, string>; lines: Record<string, string>[] }>(
    against ? '/invoice' : null,
    { no: against || undefined },
  );

  /**
   * The credit note, read back.
   *
   * `/api/sale-return` did not exist until now — the register could raise one
   * and never show it again, so the document a customer holds had no counterpart
   * anywhere in the application.
   */
  const [expanded, setExpanded] = useState<string | null>(null);
  const detail = useResource<{ header: Record<string, string>; lines: Record<string, string>[] }>(
    expanded ? '/sale-return' : null,
    { no: expanded ?? undefined },
  );

  const returnDoc = useMemo<PrintDocument | null>(() => {
    const d = detail.data;
    if (!d || !expanded) return null;
    const h = d.header;
    const currency = h.currency ?? baseCurrency();
    const money = (v: string | null | undefined) => formatMoney(v ?? '0', { currency });
    return {
      title: 'Credit Note — Sale Return',
      documentNo: h.returnNo ?? expanded,
      facts: [
        { label: 'Date', value: formatDate(h.returnDate ?? '') },
        {
          label: 'Customer',
          value: `${h.customer ?? ''}${h.customerCode ? ` (${h.customerCode})` : ''}`,
        },
        { label: 'NTN', value: h.ntn ?? '' },
        { label: 'Against invoice', value: h.againstInvoice ?? '' },
        { label: 'Returned to', value: h.warehouseName ?? h.warehouseCode ?? '' },
        { label: 'Reason', value: h.reason ?? '' },
        { label: 'Settlement', value: humanizeStatus(h.settlementMode ?? '') },
        {
          label: 'Currency',
          value: currency === baseCurrency() ? currency : `${currency} @ ${h.fxRate ?? '1'}`,
        },
      ],
      columns: [
        { label: 'Item' },
        { label: 'Batch', width: '150px' },
        { label: 'Qty', align: 'right', width: '110px' },
        { label: 'Rate', align: 'right', width: '110px' },
        { label: 'Tax', align: 'right', width: '90px' },
        { label: 'Amount', align: 'right', width: '130px' },
      ],
      rows: d.lines.map((l) => [
        `${l.sku ?? ''} · ${l.productName ?? ''}${String(l.isResaleable) === 'false' ? ' — written off' : ''}`,
        l.batchNo ?? '—',
        `${formatQty(l.enteredQty, 0)} ${l.uomCode ?? ''}`,
        formatRate(l.unitRate, 2),
        `${Number(l.taxPct ?? 0)}%`,
        money(l.lineTotal),
      ]),
      totals: [
        { label: 'Subtotal', value: money(h.subtotal) },
        ...(Number(h.taxTotal ?? 0) !== 0
          ? [{ label: 'Sales tax', value: money(h.taxTotal) }]
          : []),
        { label: 'Credited', value: money(h.grandTotal), strong: true },
        ...(currency !== baseCurrency()
          ? [{ label: `Booked in ${baseCurrency()}`, value: formatMoney(h.grandTotalBase ?? '0') }]
          : []),
      ],
      narration: h.narration ?? null,
      notes: [h.voucherNo ? `Posted as ${h.voucherNo}.` : 'Not posted to the general ledger.'],
      signatures: ['For ' + (company()?.name ?? 'us'), 'Customer acknowledgement'],
    };
  }, [detail.data, expanded]);

  /** What the return cost us back, and what it took off the salesperson. */
  const returnInternal = useMemo(
    () =>
      detail.data
        ? {
            columns: [{ label: 'COGS returned', align: 'right' as const, width: '140px' }],
            rows: detail.data.lines.map((l) => [formatMoney(l.cogsBase ?? '0', { decimals: 0 })]),
          }
        : undefined,
    [detail.data],
  );

  useEffect(() => setQuantities({}), [against]);
  useEffect(() => {
    const list = refs.data;
    if (list && warehouse === '') {
      setWarehouse(list.warehouses.find((w) => w.isDefault)?.code ?? '');
    }
  }, [refs.data, warehouse]);

  /**
   * A book with one credit-note reason has no question to ask about it.
   * Two or more and it stays a real choice.
   */
  useEffect(() => {
    const codes = refs.data?.reasonCodes ?? [];
    if (reasonCode === '' && codes.length === 1 && codes[0]) setReasonCode(codes[0].code);
  }, [refs.data, reasonCode]);

  const filters: Array<{ label: string; test: (r: SaleReturnRow) => boolean }> = [
    { label: 'All', test: () => true },
    { label: 'To main warehouse', test: (r) => r.warehouseKind !== 'DAMAGED' },
    { label: 'To damaged goods', test: (r) => r.warehouseKind === 'DAMAGED' },
    { label: 'Cash refund', test: (r) => r.settlementMode === 'CASH_REFUND' },
  ];
  const rows = useMemo(
    () => all.filter(filters[chip]?.test ?? (() => true)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all, chip],
  );

  const postable =
    !action.busy &&
    against !== '' &&
    warehouse !== '' &&
    Object.values(quantities).some((q) => q.trim() !== '' && Number(q) > 0);

  const save = () => {
    const lines = Object.entries(quantities)
      .filter(([, qty]) => qty.trim() !== '' && Number(qty) > 0)
      .map(([lineNo, qty]) => ({ lineNo: Number(lineNo), enteredQty: qty.trim() }));
    if (lines.length === 0) return;
    void action.run(() =>
      apiSend<{ returnNo: string; message: string }>(
        'POST',
        '/sale-returns',
        {
          returnDate,
          salesInvoiceNo: against,
          warehouse,
          reasonCode: reasonCode || undefined,
          settlementMode,
          lines,
        },
        auth(),
      ).then((r) => {
        setOpen(false);
        setAgainst('');
        setQuantities({});
        return r;
      }),
    );
  };

  useSubmitShortcut(open && postable, save);

  const set: RecordSet = {
    crumb: 'Sales & Purchase / Sale Return',
    title: 'Sale return / credit note',
    subtitle: 'Goods sent back by customers, credited against their invoice.',
    chips: filters.map((f) => f.label),
    cols: [
      { label: 'Credit note', width: '150px', mono: true },
      { label: 'Against invoice', width: '150px', mono: true },
      { label: 'Customer' },
      { label: 'Return warehouse', width: '180px' },
      { label: 'Qty (Kg)', align: R, mono: true, width: '110px' },
      { label: 'Value', align: R, mono: true, width: '140px' },
      { label: 'Comm. reversed', align: R, mono: true, width: '150px' },
    ],
    rows: rows.map((r) => [
      r.returnNo,
      r.againstInvoice ?? '—',
      r.customer,
      r.warehouseName,
      formatQty(r.qtyBase, 0),
      formatMoney(r.grandTotalBase, { decimals: 0 }),
      formatMoney(r.commissionReversalBase, { decimals: 0, dashOnZero: true }),
    ]),
    total: [
      '',
      '',
      `${rows.length} note${rows.length === 1 ? '' : 's'}`,
      '',
      formatQty(sumDecimal(rows.map((r) => r.qtyBase)), 0),
      formatMoney(sumDecimal(rows.map((r) => r.grandTotalBase)), { decimals: 0 }),
      formatMoney(sumDecimal(rows.map((r) => r.commissionReversalBase)), { decimals: 0 }),
    ],
  };

  const chosenReason = (refs.data?.reasonCodes ?? []).find((r) => r.code === reasonCode);

  return (
    <RecordGrid
      set={set}
      loading={res.loading}
      error={res.error}
      chip={chip}
      onChipChange={setChip}
      selectedRow={rows.findIndex((r) => r.returnNo === expanded)}
      onRowSelect={(i) =>
        setExpanded(expanded === rows[i]?.returnNo ? null : (rows[i]?.returnNo ?? null))
      }
      emptyTitle="No credit note raised yet"
      hideGrid={open}
      actions={<PanelToggle open={open} onToggle={() => setOpen(!open)} label="New credit note" />}
    >
      <Messages notice={action.notice} problem={action.problem} />

      {/* The window bar goes with the grid: it filters rows that are not on
          screen while the entry panel is open, and a stated date range over a
          form invites the reading that the form is filtered. */}
      {!open && (
        <RegisterWindow range={range} onChange={setRange} count={rows.length} noun="return" />
      )}

      {expanded && !open && (
        <DocumentPanel
          doc={returnDoc}
          internal={returnInternal}
          loading={detail.loading}
          error={detail.error}
          onClose={() => setExpanded(null)}
          actions={
            returnDoc && (
              <Button size="sm" variant="primary" onClick={() => printDocument(returnDoc)}>
                Print credit note
              </Button>
            )
          }
        />
      )}

      <EntryPanel
        title="Sale return"
        subtitle="Pick the invoice it went out on — the credit and the cost both come from that document"
        open={open}
        onToggle={() => setOpen(false)}
        openLabel="New credit note"
        // Settlement mode and Post in the header, as on the sales invoice —
        // the mode decides what the credit note does with the money, and it
        // was below the grid where nobody saw it before pressing Post.
        actions={
          <>
            {chosenReason && !chosenReason.isResaleable && (
              <Badge tone="warn">Written off — not returned to saleable stock</Badge>
            )}
            <Select
              size="sm"
              value={settlementMode}
              aria-label="Settlement mode"
              title="What the credit note does with the money"
              className="w-[190px]"
              onChange={(e) => setSettlementMode(e.target.value)}
            >
              <option value="ADJUST_RECEIVABLE">Adjust against receivable</option>
              <option value="CASH_REFUND">Cash refund</option>
              <option value="REPLACEMENT">Replacement</option>
            </Select>
            <SubmitHint />
            <Button size="sm" variant="primary" disabled={!postable} onClick={save}>
              Post credit note
            </Button>
          </>
        }
      >
        <FieldRow>
          <ComboField
            label="Against invoice"
            required
            caption="The invoice the goods went out on — the credit and the cost both come from it"
            value={against}
            options={(invoices.data?.invoices ?? [])
              .filter((i) => i.status === 'POSTED')
              .map((i) => ({
                value: i.invoiceNo,
                label: `${i.invoiceNo} · ${i.customer}`,
                hint: formatMoney(i.grandTotalBase, { decimals: 0 }),
              }))}
            onChange={setAgainst}
          />
          <TextField
            label="Return date"
            type="date"
            required
            value={returnDate}
            onChange={setReturnDate}
          />
          <ComboField
            label="Return warehouse"
            required
            hint="Damaged goods go to their own warehouse"
            value={warehouse}
            options={(refs.data?.warehouses ?? []).map((w) => ({
              value: w.code,
              label: w.name,
              hint: w.code,
            }))}
            onChange={setWarehouse}
          />
          <SelectField
            label="Reason"
            value={reasonCode}
            options={(refs.data?.reasonCodes ?? []).map((r) => ({ value: r.code, label: r.name }))}
            onChange={setReasonCode}
          />
        </FieldRow>

        {against && (
          <EntryGrid
            aria-label="Returnable lines"
            columns={[
              { key: 'no', label: 'Line', width: '70px' },
              { key: 'sku', label: 'Item' },
              { key: 'batch', label: 'Batch', width: '170px' },
              { key: 'sold', label: 'Sold', width: '130px', numeric: true },
              { key: 'returned', label: 'Already back', width: '130px', numeric: true },
              { key: 'rate', label: 'Rate', width: '110px', numeric: true },
              { key: 'ret', label: 'Return qty', width: '140px', numeric: true },
            ]}
          >
            {(source.data?.lines ?? []).map((l) => (
              <EntryRow key={l.lineNo}>
                <EntryCell padded className="text-[var(--ink-3)]">
                  {l.lineNo}
                </EntryCell>
                <EntryCell padded>
                  <span className="bb-num pr-2 text-[12px] text-[var(--accent)]">{l.sku}</span>
                  {l.productName}
                </EntryCell>
                <EntryCell padded className="bb-num text-[var(--ink-2)]">
                  {l.batchNo ?? '—'}
                </EntryCell>
                <EntryCell padded numeric>
                  {formatQty(l.enteredQty, 0)} {l.uomCode}
                </EntryCell>
                <EntryCell padded numeric>
                  {formatQty(l.returnedQtyBase, 0)}
                </EntryCell>
                <EntryCell padded numeric>
                  {formatRate(l.unitRate, 2)}
                </EntryCell>
                <EntryCell>
                  <GridInput
                    numeric
                    value={quantities[Number(l.lineNo)] ?? ''}
                    placeholder={`≤ ${formatQty(l.enteredQty, 0)}`}
                    aria-label={`Return quantity for line ${l.lineNo}`}
                    onChange={(e) =>
                      setQuantities({ ...quantities, [Number(l.lineNo)]: e.target.value })
                    }
                  />
                </EntryCell>
              </EntryRow>
            ))}
          </EntryGrid>
        )}
      </EntryPanel>
    </RecordGrid>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Commission run
// ═════════════════════════════════════════════════════════════════════════════

interface RunRow {
  runNo: string;
  businessLine: string;
  periodStart: string;
  periodEnd: string;
  salesperson: string | null;
  zoneName: string | null;
  status: string;
  recognition: string;
  personnelCount: number;
  grossSalesBase: string;
  netSalesBase: string;
  reversalBase: string;
  payableBase: string;
  calculatedAt: string | null;
  approvedAt: string | null;
  postedAt: string | null;
  voucherNo: string | null;
  narration: string | null;
  entryCount: number;
}

interface Unclaimed {
  salespersonCode: string;
  salesperson: string;
  from: string;
  to: string;
  entries: number;
  amountBase: string;
}

/**
 * Month-end: calculate → approve → post → pay.
 *
 * The run claims every unclaimed accrual in its window, which is what makes the
 * cycle safe to repeat: `commission_runs_scope_uq` refuses the same scope twice
 * for the same period, and paying a month's commission twice is a mistake nobody
 * notices until the salesperson does.
 *
 * A salesperson whose returns outweighed their sales nets to a debit, and it is
 * posted as one. That is a real overpayment to recover, not a figure to suppress.
 */
export function CommissionRunScreen() {
  const res = useResource<{ runs: RunRow[]; unclaimed: Unclaimed[] }>('/commission-runs');
  const refs = useResource<SalesRefs>('/sales-refs');
  const runs = useMemo(() => res.data?.runs ?? [], [res.data]);
  const unclaimed = useMemo(() => res.data?.unclaimed ?? [], [res.data]);
  const [chip, setChip] = useState(0);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const action = useAction(res.reload);

  const monthStart = new Date();
  const [periodStart, setPeriodStart] = useState(
    new Date(monthStart.getFullYear(), monthStart.getMonth(), 1).toISOString().slice(0, 10),
  );
  const [periodEnd, setPeriodEnd] = useState(
    new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).toISOString().slice(0, 10),
  );
  const [salespersonCode, setSalespersonCode] = useState('');

  const detail = useResource<{
    header: Record<string, string>;
    beneficiaries: Record<string, string>[];
    entries: Record<string, string>[];
  }>(expanded ? '/commission-run' : null, { no: expanded ?? undefined });

  /**
   * What a run over the window on the form would claim, read before it is opened.
   *
   * The one thing that used to go wrong here cost three button presses to find
   * out about: a window with nothing unclaimed in it produced a run that
   * calculated to nothing, approved, and was refused at **Post**. Nothing on the
   * form said the period was empty, and the refusal — "nets to nothing for every
   * salesperson" — described the arithmetic rather than the cause, so there was
   * no way to tell a wrong month from a wrong salesperson from money that had
   * already gone out on an earlier run.
   *
   * Asking first turns all of that into a sentence under the dates. It queries
   * exactly the predicate the run itself claims on, so what this says and what
   * Calculate does cannot disagree.
   */
  const preview = useResource<{
    entries: number;
    people: number;
    payableBase: string;
    claimable: Array<{ salesperson: string; entries: number; amountBase: string }>;
    nearest: { date: string; entries: number; amountBase: string } | null;
  }>(open && periodStart && periodEnd ? '/commission-run-preview' : null, {
    from: periodStart || undefined,
    to: periodEnd || undefined,
    salesperson: salespersonCode || undefined,
  });
  const nothingToClaim = preview.data !== undefined && preview.data.entries === 0;

  const filters: Array<{ label: string; test: (r: RunRow) => boolean }> = [
    { label: 'Open', test: (r) => !['POSTED', 'PAID', 'CANCELLED'].includes(r.status) },
    { label: 'Posted', test: (r) => ['POSTED', 'PAID'].includes(r.status) },
    { label: 'Cancelled', test: (r) => r.status === 'CANCELLED' },
    { label: 'All', test: () => true },
  ];
  const rows = useMemo(
    () => runs.filter(filters[chip]?.test ?? (() => true)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runs, chip],
  );

  const reload = () => {
    res.reload();
    detail.reload();
  };

  const create = () => {
    void action.run(() =>
      apiSend<{ runNo: string; message: string }>(
        'POST',
        '/commission-runs',
        { periodStart, periodEnd, salespersonCode: salespersonCode || undefined },
        auth(),
      ).then((r) => {
        setOpen(false);
        setExpanded(r.runNo);
        return r;
      }),
    );
  };

  const step = (runNo: string, verb: 'recalculate' | 'approve' | 'post' | 'cancel') => {
    void action.run(() =>
      apiSend<{ message: string }>('POST', `/commission-runs/${runNo}/${verb}`, {}, auth()).then(
        (r) => {
          reload();
          return r;
        },
      ),
    );
  };

  const set: RecordSet = {
    crumb: 'Sales & Purchase / Commission Run',
    title: 'Salesperson commission run',
    subtitle: 'Work out and approve what each salesperson has earned for the period.',
    chips: filters.map((f) => f.label),
    cols: [
      { label: 'Run', width: '150px', mono: true },
      { label: 'Period', width: '200px', mono: true },
      { label: 'Scope' },
      { label: 'People', align: R, mono: true, width: '90px' },
      { label: 'Base sales', align: R, mono: true, width: '150px' },
      { label: 'Reversals', align: R, mono: true, width: '130px' },
      { label: 'Payable', align: R, mono: true, width: '140px' },
      { label: 'Status', align: R, width: '140px', badge: true },
    ],
    rows: rows.map((r) => [
      r.runNo,
      `${formatDate(r.periodStart)} – ${formatDate(r.periodEnd)}`,
      r.salesperson ?? r.zoneName ?? 'Every salesperson',
      String(r.personnelCount),
      formatMoney(r.grossSalesBase, { decimals: 0 }),
      formatMoney(r.reversalBase, { decimals: 0, dashOnZero: true }),
      formatMoney(r.payableBase, { decimals: 0 }),
      humanizeStatus(r.status),
    ]),
    total: [
      '',
      '',
      `${rows.length} run${rows.length === 1 ? '' : 's'}`,
      '',
      formatMoney(sumDecimal(rows.map((r) => r.grossSalesBase)), { decimals: 0 }),
      formatMoney(sumDecimal(rows.map((r) => r.reversalBase)), { decimals: 0 }),
      formatMoney(sumDecimal(rows.map((r) => r.payableBase)), { decimals: 0 }),
      '',
    ],
  };

  const active = expanded ? runs.find((r) => r.runNo === expanded) : undefined;

  return (
    <RecordGrid
      set={set}
      loading={res.loading}
      error={res.error}
      chip={chip}
      onChipChange={setChip}
      onRowSelect={(i) =>
        setExpanded(expanded === rows[i]?.runNo ? null : (rows[i]?.runNo ?? null))
      }
      emptyTitle="No commission run yet"
      hideGrid={open}
      actions={<PanelToggle open={open} onToggle={() => setOpen(!open)} label="New run" />}
    >
      <Messages notice={action.notice} problem={action.problem} />

      <EntryPanel
        title="New commission run"
        subtitle="Claims every accrual in the window that no other run holds"
        open={open}
        onToggle={() => setOpen(false)}
        openLabel="New run"
        // Calculate joins the other screens' Post in the header. It was a
        // button wedged into the fourth column of the field row, which read as
        // a field rather than as the thing that runs the calculation.
        actions={
          // Disabled while the window holds nothing. Opening a run that can
          // never be posted is the mistake this screen used to make easy, and
          // the callout below says what to do instead.
          <Button
            size="sm"
            variant="primary"
            disabled={action.busy || preview.loading || nothingToClaim}
            onClick={create}
          >
            Calculate
          </Button>
        }
      >
        <FieldRow>
          <TextField
            label="Period start"
            type="date"
            required
            value={periodStart}
            onChange={setPeriodStart}
          />
          <TextField
            label="Period end"
            type="date"
            required
            value={periodEnd}
            onChange={setPeriodEnd}
          />
          <SelectField
            label="Salesperson"
            hint="Leave blank for every salesperson"
            value={salespersonCode}
            placeholder="Every salesperson"
            options={(refs.data?.salespersons ?? []).map((s) => ({ value: s.code, label: s.name }))}
            onChange={setSalespersonCode}
          />
        </FieldRow>

        {/*
          What this window holds, stated before the run exists.

          Three states and each one is a different sentence, because they are
          three different situations: money waiting to be paid, an empty window
          with money elsewhere, and an empty book. The middle one is the whole
          reason this panel exists — it names the date the accruals actually sit
          on, which is what turns "there is nothing to pay" into "you are looking
          at the wrong month".
        */}
        {preview.data && !preview.loading && (
          <Callout
            tone={nothingToClaim ? 'warn' : 'success'}
            title={
              nothingToClaim
                ? 'Nothing to claim in this period'
                : `${formatMoney(preview.data.payableBase, { decimals: 0 })} across ${preview.data.people} salesperson${preview.data.people === 1 ? '' : 's'}`
            }
          >
            {nothingToClaim ? (
              preview.data.nearest ? (
                <>
                  No commission was earned between {formatDate(periodStart)} and{' '}
                  {formatDate(periodEnd)} that another run has not already claimed. The nearest
                  unclaimed commission is dated <b>{formatDate(preview.data.nearest.date)}</b> —{' '}
                  {formatMoney(preview.data.nearest.amountBase, { decimals: 0 })} across{' '}
                  {preview.data.nearest.entries} entr
                  {preview.data.nearest.entries === 1 ? 'y' : 'ies'}. Move the dates to cover it.
                </>
              ) : (
                <>
                  There is no unclaimed commission anywhere in the book
                  {salespersonCode ? ' for this salesperson' : ''}. Commission accrues when a sales
                  invoice is posted with a salesperson on it, so raise one first — or clear the
                  salesperson filter if the invoices were somebody else&rsquo;s.
                </>
              )
            ) : (
              <>
                {preview.data.entries} accrual{preview.data.entries === 1 ? '' : 's'} will be
                claimed by this run:{' '}
                {preview.data.claimable
                  .map((c) => `${c.salesperson} ${formatMoney(c.amountBase, { decimals: 0 })}`)
                  .join(' · ')}
                . Calculate opens the run and claims them; nothing reaches the ledger until Post.
              </>
            )}
          </Callout>
        )}
      </EntryPanel>

      {active && !open && (
        <Card flush className="mb-3.5">
          <CardHeader
            title={`${active.runNo} · ${formatDate(active.periodStart)} – ${formatDate(active.periodEnd)}`}
            subtitle={
              active.voucherNo
                ? `Posted on ${active.voucherNo}`
                : `${active.entryCount} accrual${active.entryCount === 1 ? '' : 's'} claimed · ${formatMoney(active.payableBase, { decimals: 0 })} payable`
            }
            actions={
              <>
                <Button size="sm" onClick={() => setExpanded(null)}>
                  Close
                </Button>
                {['DRAFT', 'CALCULATED', 'PENDING_APPROVAL'].includes(active.status) && (
                  <Button
                    size="sm"
                    disabled={action.busy}
                    onClick={() => step(active.runNo, 'recalculate')}
                  >
                    Recalculate
                  </Button>
                )}
                {/*
                  Approve and Post are withheld from a run holding nothing,
                  rather than offered and then refused. `entryCount` is the run's
                  own claim count, so this is the same fact the server checks —
                  and the callout below the header says what to do instead. The
                  server still refuses; this is what stops it being reached.
                */}
                {['CALCULATED', 'PENDING_APPROVAL'].includes(active.status) &&
                  active.entryCount > 0 && (
                    <Button
                      size="sm"
                      disabled={action.busy}
                      onClick={() => step(active.runNo, 'approve')}
                    >
                      Approve
                    </Button>
                  )}
                {active.status === 'APPROVED' && active.entryCount > 0 && (
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={action.busy}
                    onClick={() => step(active.runNo, 'post')}
                  >
                    Post to Commission Payable
                  </Button>
                )}
                {!['POSTED', 'PAID', 'CANCELLED'].includes(active.status) && (
                  <Button
                    size="sm"
                    disabled={action.busy}
                    onClick={() => step(active.runNo, 'cancel')}
                  >
                    Cancel
                  </Button>
                )}
              </>
            }
          />
          {/*
            An empty run explained where it is, instead of at Post. It is not an
            error — it is a run over a period nothing was earned in — so it says
            what the two real options are and stops there.
          */}
          {active.entryCount === 0 && !['POSTED', 'PAID', 'CANCELLED'].includes(active.status) && (
            <CardBody>
              <Callout
                tone="warn"
                title="This run holds no commission, so there is nothing to post"
              >
                No commission was earned between {formatDate(active.periodStart)} and{' '}
                {formatDate(active.periodEnd)} that another run had not already claimed.{' '}
                <b>Recalculate</b> if sales invoices have been raised since this run was opened —
                they will join it. Otherwise <b>Cancel</b> it and open a run over the period the
                invoices actually fall in; cancelling releases nothing, because it is holding
                nothing.
              </Callout>
            </CardBody>
          )}

          <EntryGrid
            aria-label={`Beneficiaries of ${active.runNo}`}
            columns={[
              { key: 'sp', label: 'Salesperson' },
              { key: 'zone', label: 'Zone', width: '170px' },
              { key: 'n', label: 'Entries', width: '100px', numeric: true },
              { key: 'base', label: 'Base sales', width: '160px', numeric: true },
              { key: 'acc', label: 'Accrued', width: '150px', numeric: true },
              { key: 'rev', label: 'Reversed', width: '140px', numeric: true },
              { key: 'pay', label: 'Payable', width: '150px', numeric: true },
            ]}
          >
            {(detail.data?.beneficiaries ?? []).map((b, i) => (
              <EntryRow key={i}>
                <EntryCell padded>{b.salesperson}</EntryCell>
                <EntryCell padded className="text-[var(--ink-2)]">
                  {b.zoneName ?? '—'}
                </EntryCell>
                <EntryCell padded numeric>
                  {b.entries}
                </EntryCell>
                <EntryCell padded numeric>
                  {formatMoney(b.baseAmount, { decimals: 0 })}
                </EntryCell>
                <EntryCell padded numeric>
                  {formatMoney(b.accruedBase, { decimals: 0 })}
                </EntryCell>
                <EntryCell padded numeric className="text-[var(--debit)]">
                  {formatMoney(b.reversedBase, { decimals: 0, dashOnZero: true })}
                </EntryCell>
                <EntryCell padded numeric className="font-medium">
                  {formatMoney(b.payableBase, { decimals: 0 })}
                </EntryCell>
              </EntryRow>
            ))}
          </EntryGrid>
        </Card>
      )}

      {/* Register totals go with the register — see `hideGrid`. */}
      {!open && (
        <Tiles>
          <Card>
            <StatTile
              label="Unclaimed accrual"
              value={formatMoney(sumDecimal(unclaimed.map((u) => u.amountBase)), { decimals: 0 })}
              hint={`${unclaimed.length} salesperson(s) waiting for a run`}
            />
          </Card>
          <Card>
            <StatTile
              label="Payable this view"
              value={formatMoney(sumDecimal(rows.map((r) => r.payableBase)), { decimals: 0 })}
              hint="after reversals"
            />
          </Card>
          <Card>
            <StatTile
              label="Reversed by returns"
              value={formatMoney(sumDecimal(rows.map((r) => r.reversalBase)), { decimals: 0 })}
              hint="pro rata on credit notes"
            />
          </Card>
          <Card>
            <StatTile
              label="Effective rate"
              value={formatPercent(
                Number(sumDecimal(rows.map((r) => r.grossSalesBase))) > 0
                  ? (
                      (Number(sumDecimal(rows.map((r) => r.payableBase))) /
                        Number(sumDecimal(rows.map((r) => r.grossSalesBase)))) *
                      100
                    ).toFixed(4)
                  : '0',
              )}
              hint="payable over base sales"
            />
          </Card>
        </Tiles>
      )}

      {unclaimed.length > 0 && (
        <Card flush className="mt-3.5" data-noprint="1">
          <CardHeader
            title="Waiting for a run"
            subtitle="Accruals no run has claimed — the next run over their dates will pick them up"
          />
          <CardBody className="flex flex-col gap-2">
            {unclaimed.map((u) => (
              <div key={u.salespersonCode} className="flex justify-between text-[12.5px]">
                <span>
                  {u.salesperson}
                  <span className="pl-2 text-[var(--ink-3)]">
                    {formatDate(u.from)} – {formatDate(u.to)} · {u.entries} entr
                    {u.entries === 1 ? 'y' : 'ies'}
                  </span>
                </span>
                <span className="bb-num tabular-nums">
                  {formatMoney(u.amountBase, { decimals: 0 })}
                </span>
              </div>
            ))}
          </CardBody>
        </Card>
      )}
    </RecordGrid>
  );
}
